import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { Role } from '../types';

/**
 * Generates a flat array representing the hierarchical structure of the Accountability Chart
 */
const flattenRolesForExport = (roles: Role[]): any[] => {
  // Sort roles to try to get a top-down structure
  const sortedRoles = [...roles].sort((a, b) => {
    if (a.level !== b.level) {
      return a.level - b.level;
    }
    return a.sortOrder - b.sortOrder;
  });

  return sortedRoles.map(role => ({
    'Title': role.title,
    'Owner / Person': role.ownerName || 'Vacant',
    'Level': role.level === 0 ? 'Visionary' : role.level === 1 ? 'Integrator' : `Dept/Team Level ${role.level}`,
    'Responsibilities': role.responsibilities.join('; '),
    'Subtitle': role.subtitle || '',
    'ID (Internal)': role.id,
    'Parent ID (Internal)': role.parentId || 'Root',
  }));
};

/**
 * Exports the Accountability Chart as a CSV file
 */
export const exportAccountabilityChartCSV = (roles: Role[]) => {
  if (!roles || roles.length === 0) {
    alert('No roles available to export.');
    return;
  }

  const flattenedData = flattenRolesForExport(roles);
  
  const csv = Papa.unparse(flattenedData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, 'Accountability_Chart_Export.csv');
};

/**
 * Creates a PDF screenshot of the target element. 
 * Handles temporarily restructuring the DOM as needed so html2canvas captures full width un-clipped.
 */
export const exportAccountabilityChartPDF = async (targetRef: React.RefObject<HTMLDivElement>) => {
  if (!targetRef.current) {
    alert("Could not find the chart container to generate PDF.");
    return;
  }

  const captureElement = targetRef.current;
  
  // Store original styles that might restrict clipping
  const originalOverflow = captureElement.style.overflow;
  const originalMaxW = captureElement.style.maxWidth;
  const originalMaxH = captureElement.style.maxHeight;

  try {
    // Prep element for full render
    captureElement.style.overflow = 'visible';
    captureElement.style.maxWidth = 'none';
    captureElement.style.maxHeight = 'none';

    // We scale by 2 to get a high DPI canvas
    const canvas = await html2canvas(captureElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');

    // Create A4 PDF (landscape)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    // A4 Landscape dimensions
    const pdfWidth = 297; 
    const pdfHeight = 210;

    // Calculate ratio to fit canvas proportionally within A4
    const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
    const scaledWidth = canvas.width * ratio;
    const scaledHeight = canvas.height * ratio;

    // Calculate margins to center the image
    const marginX = (pdfWidth - scaledWidth) / 2;
    const marginY = (pdfHeight - scaledHeight) / 2;

    pdf.addImage(imgData, 'PNG', marginX, marginY, scaledWidth, scaledHeight);
    
    // Add title/footer
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    pdf.text('EOS Accountability Chart', 10, pdfHeight - 10);
    
    // YYYY-MM-DD
    const dateStr = new Date().toISOString().split('T')[0];
    pdf.text(dateStr, pdfWidth - 30, pdfHeight - 10);

    pdf.save(`Accountability_Chart_${dateStr}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("There was an error generating the PDF export.");
  } finally {
    // Restore original CSS
    captureElement.style.overflow = originalOverflow;
    captureElement.style.maxWidth = originalMaxW;
    captureElement.style.maxHeight = originalMaxH;
  }
};
